import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3-hierarchy';
import ReactECharts from 'echarts-for-react';
import _ from 'lodash';
import PropTypes from 'prop-types';

const CirclePackingChart =  (props) => {
    const {
        rawData,
        titleName = '',
        showTitle = false,
        color ,
        events = false,
         } = props;
    const chartRef = useRef(null);

    useEffect(() => {
        if (rawData) {
            run(rawData);
        }
    }, [rawData]);

    function run(rawData) {
        const dataWrap = prepareData(rawData);
        initChart(dataWrap.seriesData, dataWrap.maxDepth);
    }

    function prepareData(rawData) {
        const seriesData = [];
        let maxDepth = 0;

        function convert(source, basePath, depth) {
            if (source == null) {
                return;
            }
            if (maxDepth > 5) {
                return;
            }
            maxDepth = Math.max(depth, maxDepth);
            seriesData.push({
                id: basePath,
                value: source.$count,
                depth: depth,
                index: seriesData.length,
            });
            for (var key in source) {
                if (source.hasOwnProperty(key) && !key.match(/^\$/)) {
                    var path = basePath + '.' + key;
                    convert(source[key], path, depth + 1);
                }
            }
        }

        convert(rawData, 'option', 0);

        return {
            seriesData: seriesData,
            maxDepth: maxDepth,
        };
    }
    function toTitleCase(str) {
        return str.replace(/\w\S*/g, function (txt) {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        });
    }
    function initChart(seriesData, maxDepth) {
        var displayRoot = stratify();

        function stratify() {
            return d3
                .stratify()
                .parentId(function (d) {
                    return d.id.substring(0, d.id.lastIndexOf('.'));
                })(seriesData)
                .sum(function (d) {
                    return d.value || 0;
                })
                .sort(function (a, b) {
                    return b.value - a.value;
                });
        }

        function overallLayout(params, api) {
            var context = params.context;
            d3
                .pack()
                .size([api.getWidth() - 2, api.getHeight() - 2])
                .padding(3)(displayRoot);
            context.nodes = {};
            displayRoot.descendants().forEach(function (node, index) {
                context.nodes[node.id] = node;
            });
        }

        function renderItem(params, api) {
            var context = params.context;

            if (!context.layout) {
                context.layout = true;
                overallLayout(params, api);
            }

            var nodePath = api.value('id');
            var node = context.nodes[nodePath];
            if (!node) {
                return;
            }

            var isLeaf = !node.children || !node.children.length;
            var focus = new Uint32Array(
                node.descendants().map(function (node) {
                    return node.data.index;
                })
            );

            var nodeName = isLeaf
                ? nodePath
                    .slice(nodePath.lastIndexOf('.') + 1)
                    .split(/(?=[A-Z][^A-Z])/g)
                    .join('\n')
                : '';
                nodeName = toTitleCase(nodeName); 
            var z2 = api.value('depth') * 2;

            var textColor =  '#000000';

            var packColor = color && color[node.data.id] !== undefined ? color[node.data.id] : '#fff';

             var borderColor = '#8C8D8E';

            return {
                type: 'circle',
                focus: focus,
                shape: {
                    cx: node.x,
                    cy: node.y,
                    r: node.r,
                },
                transition: ['shape'],
                z2: z2,
                textContent: {
                    type: 'text',
                    style: {
                        text: nodeName,
                        // fontFamily: 'Arial',
                        width: node.r * 1.3,
                        overflow: 'visisble',
                        fontSize: node.r / 3,
                        fill: textColor,
                    },
                    emphasis: {
                        style: {
                            overflow: null,
                            fontSize: Math.max(node.r / 3, 12),
                        },
                    },
                },
                textConfig: {
                    position: 'inside',
                },
                style: {
                    fill: packColor,
                    stroke: borderColor,
                },
                emphasis: {
                    style: {
                        fontFamily: 'Arial',
                        fontSize: 12,
                        shadowBlur: 20,
                        shadowOffsetX: 3,
                        shadowOffsetY: 5,
                        shadowColor: 'rgba(0,0,0,0.3)',
                    },
                },
            };
        }

        var option = {
            dataset: {
                source: seriesData,
            },
            title:{
                show: showTitle,
                text:titleName,
                x:"center",
                // y:2,
                textStyle: {
                fontSize: 10,
                }
            },
            tooltip: {
                position :"top",
                formatter: function (params) {
                    const idParts = params.value.id.split('.');
                    let lastPart = idParts[idParts.length - 1];
        
                    if (lastPart.startsWith('option.')) {
                        lastPart = lastPart.slice('option.'.length);
                    }
                    const formattedId = lastPart === 'option' ? 'Total Items' : lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
                    return `${formattedId}: ${params.value.value}`;
                },
            },
            
            legend: {
                show: true,  
                orient: 'vertical',  
                left: 'left',  
                data: ["Dept","Risks"] , 
            },
            visualMap: [
                {
                    show: false,
                    min: 0,
                    max: maxDepth,
                    dimension: 'depth',
                    inRange: {
                        color: ['#006edd', '#e0ffff'],
                    },
                },
            ],
            hoverLayerThreshold: Infinity,
            series: [
                {
                    type: 'custom',
                    renderItem: renderItem,
                    progressive: 0,
                    coordinateSystem: 'none',
                    encode: {
                        tooltip: 'value',
                        itemName: 'id',
                    },
                },
            ],
        };
        chartRef.current.getEchartsInstance().setOption(option);
    }

    const handleChartClick = (params) => {
        const idParts = params.value.id.split('.');
        let lastPart = idParts[idParts.length - 1];

        if (lastPart.startsWith('option.')) {
            lastPart = lastPart.slice('option.'.length);
        }
        const taskType = lastPart === 'option' ? 'Total Items' : lastPart.charAt(0) + lastPart.slice(1);
        const clickedSprint = props.titleName[0];  
        const idValue = params.dataIndex;
        if (props.onSprintClick && idValue!=0) {
          props.onSprintClick(clickedSprint,taskType,idValue);
        }
    };
      
      
      const handleLegendSelectChanged = (params) => {
       };
    
      const onEvents = {
        'click': handleChartClick,
        'legendselectchanged': handleLegendSelectChanged,
      };
    return (
        <ReactECharts
            ref={chartRef}
            option={{}}
            onEvents={events ? onEvents : ""}
            style={{ height: '150px' }}
        />
    );
};


function areEqual(prevProps, nextProps) {
    if(_.isEqual(prevProps.rawData, nextProps.rawData) &&
    _.isEqual(prevProps.titleName, nextProps.titleName) &&
    _.isEqual(prevProps.showTitle, nextProps.showTitle) &&
    _.isEqual(prevProps.color, nextProps.color) &&
    _.isEqual(prevProps.events, nextProps.events)) {
        return true;
    } else {
        return false;
    }
}

export default React.memo(CirclePackingChart, areEqual);
CirclePackingChart.propTypes = {
    rawData: PropTypes.object.isRequired,
    titleName: PropTypes.string,
    showTitle: PropTypes.bool,
    color: PropTypes.object,
    events: PropTypes.bool,
    toolTipData: PropTypes.any,
    onSprintClick: PropTypes.func,
  };
  


